import {
  CartErrorEvent,
  CartLinesUpdateEvent,
  CartViewEvent,
} from 'https://cdn.shopify.com/storefront/standard-events.js';

(() => {
  'use strict';

  const VERSION = '2026-08-07';
  const DEFAULT_CURRENCY = 'AUD';
  const UPDATE_TIMEOUT_MS = 10000;
  let cartCache = null;
  let pendingUpdate = null;
  let fullCartPageViewed = false;

  const money = (cents) => Number(cents || 0) / 100;

  const fetchCart = async () => {
    const response = await fetch('/cart.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error('Unable to read cart');
    const cart = await response.json();
    cartCache = cart;
    return cart;
  };

  const standardCart = (cart) => CartLinesUpdateEvent.createCartFromAjaxResponse(cart);

  const cartItemToGa = (item, quantity, index = 0) => ({
    item_id: String(item?.variant_id || item?.id || item?.key || ''),
    item_name: item?.product_title || item?.title || 'Wine',
    item_brand: item?.vendor || 'Weathercraft',
    item_variant: item?.variant_title || undefined,
    price: money(item?.final_price ?? item?.price ?? item?.original_price ?? 0),
    quantity,
    index,
  });

  const emitGaRemoval = (operation, updatedCart) => {
    if (!operation.emitGa4 || !operation.previousItem) return;

    const remaining = updatedCart.items.find((item) => item.key === operation.key);
    const remainingQuantity = Number(remaining?.quantity || 0);
    const removedQuantity = operation.previousQuantity - remainingQuantity;
    if (removedQuantity <= 0) return;

    const item = cartItemToGa(operation.previousItem, removedQuantity, operation.index);
    window.WeathercraftPhase2Analytics?.emit?.('remove_from_cart', {
      currency: updatedCart.currency || operation.currency || DEFAULT_CURRENCY,
      value: item.price * removedQuantity,
      items: [item],
      cart_remove_source: operation.context === 'dialog' ? 'cart_drawer' : 'cart_page',
      measurement_source: 'weathercraft_native_cart_events',
      measurement_version: VERSION,
    });
  };

  const detailsFromTarget = (target, fallbackLine) => {
    if (!(target instanceof Element)) return null;

    const component = target.closest('cart-items-component');
    const row = target.closest('[data-key]');
    if (!component || !row) return null;

    const key = row.dataset.key || '';
    const line = Number(fallbackLine || 0);
    const previousItem =
      cartCache?.items?.find((item) => item.key === key) ||
      (line > 0 ? cartCache?.items?.[line - 1] : undefined);
    const input = row.querySelector('input[data-cart-quantity], input[type="number"]');
    const previousQuantity = Number(
      previousItem?.quantity ??
        input?.getAttribute('data-cart-quantity') ??
        input?.defaultValue ??
        input?.value ??
        0
    );
    const rows = Array.from(component.querySelectorAll('[data-key]'));

    return {
      component,
      context: component.dataset.drawer !== undefined || component.closest('cart-drawer-component') ? 'dialog' : 'cart',
      index: Math.max(0, rows.indexOf(row)),
      key,
      line,
      previousItem: previousItem ? { ...previousItem } : null,
      previousQuantity,
    };
  };

  const dispatchStandardError = (operation, message, code = 'SERVICE_UNAVAILABLE') => {
    operation.target.dispatchEvent(
      new CartErrorEvent({
        error: message,
        code,
      })
    );
  };

  const rejectPending = (message, code = 'SERVICE_UNAVAILABLE') => {
    if (!pendingUpdate) return;
    const operation = pendingUpdate;
    pendingUpdate = null;
    window.clearTimeout(operation.timer);
    dispatchStandardError(operation, message, code);
    operation.deferred.reject(new Error(message));
  };

  const startCartUpdate = ({ details, quantity, source, emitGa4 }) => {
    const nextQuantity = Number(quantity);
    if (!Number.isFinite(nextQuantity) || nextQuantity < 0) return;

    if (
      pendingUpdate &&
      pendingUpdate.key === details.key &&
      Date.now() - pendingUpdate.startedAt < 1000
    ) {
      return;
    }

    if (pendingUpdate) rejectPending('Cart update was superseded by a newer action', 'INVALID');

    const action = nextQuantity < details.previousQuantity ? 'remove' : 'update';
    if (action === 'update' && nextQuantity === details.previousQuantity) return;

    const deferred = CartLinesUpdateEvent.createPromise();
    deferred.promise.catch(() => {});

    details.component.dispatchEvent(
      new CartLinesUpdateEvent({
        action,
        context: details.context,
        lines: [
          {
            id: details.key || String(details.line || details.index + 1),
            quantity: nextQuantity,
          },
        ],
        promise: deferred.promise,
      })
    );

    const operation = {
      ...details,
      action,
      currency: cartCache?.currency || DEFAULT_CURRENCY,
      deferred,
      emitGa4: Boolean(emitGa4 && action === 'remove'),
      source,
      startedAt: Date.now(),
      target: details.component,
      timer: null,
    };

    operation.timer = window.setTimeout(() => {
      if (pendingUpdate === operation) {
        rejectPending('Cart update did not complete in time');
      }
    }, UPDATE_TIMEOUT_MS);

    pendingUpdate = operation;
  };

  const handleQuantityIntent = (event) => {
    const quantity = Number(event.detail?.quantity);
    const line = Number(event.detail?.cartLine || 0);
    const details = detailsFromTarget(event.target, line);
    if (!details || !Number.isFinite(quantity)) return;

    startCartUpdate({
      details,
      quantity,
      source: 'quantity-selector',
      emitGa4: quantity < details.previousQuantity,
    });
  };

  const handleRemoveIntent = (event) => {
    if (!(event.target instanceof Element)) return;
    const button = event.target.closest('.cart-items__remove');
    if (!button) return;

    const details = detailsFromTarget(button, 0);
    if (!details) return;

    startCartUpdate({
      details,
      quantity: 0,
      source: event.type === 'pointerdown' ? 'remove-button-pointer' : 'remove-button-keyboard',
      // Pointer removals are already handled by the Phase 3 GA4 measurement repair.
      // The click fallback covers keyboard activation without duplicating pointer GA4 events.
      emitGa4: event.type === 'click' && event.detail === 0,
    });
  };

  const handleCartUpdate = (event) => {
    const updatedCart = event.detail?.resource;
    if (!updatedCart || !Array.isArray(updatedCart.items)) return;

    const operation = pendingUpdate;
    if (operation) {
      pendingUpdate = null;
      window.clearTimeout(operation.timer);
      try {
        operation.deferred.resolve({ cart: standardCart(updatedCart) });
        emitGaRemoval(operation, updatedCart);
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        dispatchStandardError(operation, message, 'INVALID');
        operation.deferred.reject(error);
      }
    }

    cartCache = updatedCart;
  };

  const cartViewTarget = (context, preferredTarget) => {
    if (preferredTarget instanceof Element) return preferredTarget;
    if (context === 'dialog') return document.querySelector('cart-drawer-component, cart-drawer');
    return (
      document.querySelector('cart-items-component:not([data-drawer])') ||
      document.querySelector('main') ||
      document.body
    );
  };

  const dispatchCartView = async (context, preferredTarget) => {
    try {
      const cart = await fetchCart();
      const target = cartViewTarget(context, preferredTarget);
      target?.dispatchEvent(
        new CartViewEvent({
          context,
          cart: standardCart(cart),
        })
      );
    } catch (error) {
      console.warn('[Weathercraft native cart events] cart view skipped:', error);
    }
  };

  const initialise = () => {
    if (window.location.pathname === '/cart' && !fullCartPageViewed) {
      fullCartPageViewed = true;
      dispatchCartView('page');
    } else {
      fetchCart().catch(() => null);
    }
  };

  document.addEventListener('quantity-selector:update', handleQuantityIntent, true);
  document.addEventListener('pointerdown', handleRemoveIntent, true);
  document.addEventListener('click', handleRemoveIntent, true);
  document.addEventListener('cart:update', handleCartUpdate, true);
  document.addEventListener('cart:error', (event) => {
    if (!pendingUpdate) return;
    const message = event.detail?.data?.message || 'Cart update failed';
    rejectPending(message, 'INVALID');
  }, true);
  document.addEventListener('dialog:open', (event) => {
    if (!(event.target instanceof Element) || !event.target.matches('cart-drawer-component')) return;
    dispatchCartView('dialog', event.target);
  }, true);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialise, { once: true });
  } else {
    initialise();
  }
})();
